/**
 * Shared UI for the tap/observe letter engines (GenericTap, BubblePop,
 * CatchFalling, LetterHunt, PictureGrid, StoryPages, CallSequence,
 * Discovery, SentenceMeaning). Everything sizes off the stage's `--tile`
 * custom property and styles through LEARN tokens.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { motion, useAnimationControls } from "framer-motion";
import { LEARN } from "../../theme";
import { learnAssetMeta } from "../../media/learnAssets";
import { CountPill, ensureKeyframes, resolveAsset, useLetterAccent } from "./engineUtils";

export { useLetterAccent };

/* ── Tokens ─────────────────────────────────────────────────────────── */

/** Every engine root: fills the arena, never overflows it. */
export const ROOT_STYLE = {
  flex: 1,
  minHeight: 0,
  minWidth: 0,
  position: "relative",
  display: "flex",
  flexDirection: "column",
  fontFamily: LEARN.font,
  color: LEARN.color.ink,
  userSelect: "none",
  WebkitUserSelect: "none",
};

/** Frosted white panel that stays legible over the world art. */
export function frost(extra = {}) {
  return {
    background: "rgba(255,255,255,0.92)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    borderRadius: LEARN.radius.lg,
    boxShadow: `${LEARN.shadow.card}, inset 0 1px 0 rgba(255,255,255,1)`,
    border: `2px solid ${LEARN.color.line}`,
    ...extra,
  };
}

/**
 * Accent header strip for a frosted panel: a tinted band with a small badge
 * and a short title, sitting flush at the top of the panel.
 */
export function PanelHeader({ accent = LEARN.color.blue, deep = LEARN.color.blueDeep, badge, title, right, compact = false, style }) {
  return (
    <div
      style={{
        display: "flex", alignItems: "center", gap: compact ? 8 : 10, minWidth: 0,
        padding: compact ? "6px 8px 6px 8px" : "8px 12px 8px 10px",
        background: `linear-gradient(180deg, ${accent}24 0%, ${accent}12 100%)`,
        borderBottom: `2px solid ${accent}22`,
        ...style,
      }}
    >
      {badge != null && (
        <span
          aria-hidden="true"
          style={{
            width: compact ? 26 : 30, height: compact ? 26 : 30, borderRadius: compact ? 8 : 9, background: accent, color: "#fff",
            display: "grid", placeItems: "center", fontWeight: 700, fontSize: compact ? 15 : 17, flexShrink: 0, boxShadow: `0 2px 0 ${deep}`,
          }}
        >
          {badge}
        </span>
      )}
      <span style={{ flex: 1, minWidth: 0, fontFamily: LEARN.font, fontWeight: 700, fontSize: compact ? 14 : 16, color: deep, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {title}
      </span>
      {right}
    </div>
  );
}

export function tileSize(mult = 1) {
  return `calc(var(--tile, 72px) * ${mult})`;
}

/** Resolve a promise or give up after `ms` (audio that never unlocks must not hang a game). */
export function withTimeout(promise, ms) {
  return new Promise((resolve) => {
    let done = false;
    const t = setTimeout(() => { if (!done) { done = true; resolve(false); } }, ms);
    Promise.resolve(promise).then(
      (v) => { if (!done) { done = true; clearTimeout(t); resolve(v); } },
      () => { if (!done) { done = true; clearTimeout(t); resolve(false); } },
    );
  });
}

export function wait(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

/** Hosted word art for a letter word if the manifest has it, else null. */
export function letterWordArt(letter, word) {
  const l = String(letter || "").toLowerCase();
  const w = String(word || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  if (!l || !w) return null;
  const key = `${l}_word_${w}`;
  return learnAssetMeta(`letters/${l}/${key}`) ? key : null;
}

/** Escape a word for use inside a RegExp. */
export function escapeRe(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/* ── Measurement ────────────────────────────────────────────────────── */

/** Live size of an element plus the resolved `--tile` px (0 until measured). */
export function useArenaMetrics(ref) {
  const [m, setM] = useState({ w: 0, h: 0, tile: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const read = () => {
      const r = el.getBoundingClientRect();
      const raw = typeof getComputedStyle === "function" ? getComputedStyle(el).getPropertyValue("--tile") : "";
      const tile = parseFloat(raw) || 0;
      setM((prev) => (prev.w === r.width && prev.h === r.height && prev.tile === tile ? prev : { w: r.width, h: r.height, tile }));
    };
    read();
    // --tile settles after the stage measures its arena; a size observer
    // never fires for a custom-property change, so re-read it shortly after.
    const raf = typeof requestAnimationFrame === "function" ? requestAnimationFrame(() => requestAnimationFrame(read)) : 0;
    const late = setTimeout(read, 260);
    if (typeof ResizeObserver === "undefined") return () => { clearTimeout(late); if (raf) cancelAnimationFrame(raf); };
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => { ro.disconnect(); clearTimeout(late); if (raf) cancelAnimationFrame(raf); };
  }, [ref]);
  return m;
}

/** `:focus-visible` guard (older engines / jsdom throw on the selector). */
function isKeyboardFocus(el) {
  try { return !!el?.matches?.(":focus-visible"); } catch { return false; }
}

/* ── Icons (inline SVG only) ────────────────────────────────────────── */

export function IconCheck({ size = 18, color = "#fff", stroke = 3.2 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
  );
}

export function IconArrow({ size = 22, color = "#fff", dir = "right" }) {
  const rot = dir === "left" ? 180 : dir === "up" ? -90 : dir === "down" ? 90 : 0;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ transform: `rotate(${rot}deg)` }}>
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

export function IconSpeaker({ size = 20, color = "#fff" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 10v4h3l4 4V6L7 10H4z" fill={color} />
      <path d="M15 9.5a3.5 3.5 0 010 5" />
      <path d="M17.5 7a7 7 0 010 10" />
    </svg>
  );
}

export function IconStar({ size = 22, color = LEARN.color.sun, filled = true, stroke = LEARN.color.sunDeep }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M12 2.8l2.8 5.9 6.4.8-4.7 4.4 1.2 6.4L12 17.2l-5.7 3.1 1.2-6.4L2.8 9.5l6.4-.8L12 2.8z"
        fill={filled ? color : "rgba(11,26,51,0.08)"}
        stroke={filled ? stroke : "rgba(11,26,51,0.18)"}
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function IconSparkle({ size = 26, color = "#fff" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} aria-hidden="true">
      <path d="M12 2c.6 4.4 2.6 7.4 8 8-5.4.6-7.4 3.6-8 8-.6-4.4-2.6-7.4-8-8 5.4-.6 7.4-3.6 8-8z" />
      <path d="M19 15c.3 1.8 1.2 2.7 3 3-1.8.3-2.7 1.2-3 3-.3-1.8-1.2-2.7-3-3 1.8-.3 2.7-1.2 3-3z" opacity="0.85" />
    </svg>
  );
}

/* ── Building blocks ─────────────────────────────────────────────────── */

/** Word cutout art (hosted WebP / legacy PNG). Falls back to the word itself. */
export function Art({ letter, assetName, word, alt, style }) {
  const src = resolveAsset(letter, assetName);
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [src]);
  if (!src || failed) {
    return (
      <span style={{ fontWeight: 700, fontSize: "clamp(16px, 1.1em, 24px)", lineHeight: 1.1, textAlign: "center", padding: 4, color: LEARN.color.ink, ...style }}>
        {word || alt || ""}
      </span>
    );
  }
  return (
    <img
      src={src}
      alt={alt || ""}
      aria-hidden={alt ? undefined : "true"}
      draggable={false}
      decoding="async"
      onError={() => setFailed(true)}
      style={{ width: "80%", height: "80%", objectFit: "contain", pointerEvents: "none", filter: "drop-shadow(0 4px 6px rgba(11,26,51,0.14))", ...style }}
    />
  );
}

/**
 * White sticker tile: real <button>, `--tile`-sized, art at ~80%, lift on
 * hover/press, spring pop-in. Drive feedback with the `shake` / `pulse`
 * counters (increment to replay) and `state` for colour.
 */
export function StickerTile({
  letter,
  assetName,
  word,
  label,
  size = 1,
  state = "idle", // idle | correct | done | wrong | dim
  onTap,
  disabled = false,
  ariaLabel,
  reduceMotion = false,
  accent = LEARN.color.blue,
  shake = 0,
  pulse = 0,
  index = 0,
  layoutId,
  style,
  children,
  badge,
  square = true,
  rotate = 0,
  ...rest
}) {
  const controls = useAnimationControls();
  const [focusRing, setFocusRing] = useState(false);
  useEffect(ensureKeyframes, []);

  useEffect(() => {
    if (reduceMotion) { controls.set({ scale: 1, opacity: 1, x: 0, rotate }); return; }
    controls.start({ scale: 1, opacity: 1, x: 0, rotate, transition: { type: "spring", stiffness: 420, damping: 24, delay: Math.min(index, 16) * 0.035 } });
    // mount only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rotRef = useRef(rotate);
  useEffect(() => {
    if (rotRef.current === rotate) return;
    rotRef.current = rotate;
    if (reduceMotion) controls.set({ rotate });
    else controls.start({ rotate, transition: { type: "spring", stiffness: 300, damping: 20 } });
  }, [rotate, reduceMotion, controls]);

  useEffect(() => {
    if (!shake || reduceMotion) return;
    controls.start({ x: [0, -9, 9, -6, 6, 0], transition: { duration: 0.42, ease: "easeInOut" } });
  }, [shake, reduceMotion, controls]);

  useEffect(() => {
    if (!pulse || reduceMotion) return;
    controls.start({ scale: [1, 1.16, 1], transition: { duration: 0.42, ease: "easeOut" } });
  }, [pulse, reduceMotion, controls]);

  const ring =
    state === "correct" ? `0 0 0 3px ${accent}` :
    state === "wrong" ? `0 0 0 3px ${LEARN.color.coral}` :
    focusRing ? `0 0 0 3px ${LEARN.color.blue}` : "none";
  const bg =
    state === "correct" ? `linear-gradient(180deg, ${accent}10 0%, ${accent}22 100%)` :
    state === "done" ? "rgba(255,255,255,0.7)" : "linear-gradient(180deg, #FFFFFF 0%, #F6F8FC 100%)";
  const w = tileSize(size);

  return (
    <motion.button
      type="button"
      layoutId={layoutId}
      initial={reduceMotion ? false : { scale: 0.6, opacity: 0, rotate }}
      animate={controls}
      whileHover={!disabled && !reduceMotion ? { y: -3 } : undefined}
      whileTap={!disabled && !reduceMotion ? { scale: 0.94, y: 1 } : undefined}
      onClick={disabled ? undefined : onTap}
      disabled={disabled}
      aria-label={ariaLabel || word || label}
      aria-pressed={state === "done" ? true : undefined}
      onFocus={(e) => setFocusRing(isKeyboardFocus(e.currentTarget))}
      onBlur={() => setFocusRing(false)}
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        width: w,
        minWidth: w,
        height: square && !label ? w : "auto",
        minHeight: square ? w : undefined,
        padding: label ? "6px 6px 8px" : 0,
        boxSizing: "border-box",
        borderRadius: `calc(var(--tile, 72px) * ${size} * 0.22)`,
        background: bg,
        border: `2px solid ${state === "correct" ? `${accent}55` : LEARN.color.line}`,
        boxShadow: `${LEARN.shadow.card}, inset 0 1px 0 rgba(255,255,255,1)${ring !== "none" ? `, ${ring}` : ""}`,
        opacity: state === "dim" ? 0.42 : state === "done" ? 0.72 : 1,
        cursor: disabled ? "default" : "pointer",
        outline: "none",
        WebkitTapHighlightColor: "transparent",
        touchAction: "manipulation",
        fontFamily: LEARN.font,
        color: LEARN.color.ink,
        transition: reduceMotion ? "none" : "background 180ms, opacity 220ms, box-shadow 180ms",
        ...style,
      }}
      {...rest}
    >
      <span style={{ width: "100%", aspectRatio: "1 / 1", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        {children !== undefined ? children : <Art letter={letter} assetName={assetName} word={word} alt="" />}
      </span>
      {label && (
        <span style={{ fontSize: `clamp(14px, calc(var(--tile, 72px) * ${size} * 0.17), 22px)`, fontWeight: 700, lineHeight: 1.1, textAlign: "center", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%", padding: "0 2px" }}>
          {label}
        </span>
      )}
      {badge}
      {state === "done" && !badge && (
        <span
          aria-hidden="true"
          style={{ position: "absolute", top: -6, right: -6, width: 26, height: 26, borderRadius: "50%", background: LEARN.color.mint, boxShadow: LEARN.shadow.sticker(LEARN.color.mintDeep), display: "grid", placeItems: "center" }}
        >
          <IconCheck size={15} />
        </span>
      )}
    </motion.button>
  );
}

/** Round chunky sticker button for speaker / arrow controls. */
export function RoundButton({ onClick, ariaLabel, children, accent = LEARN.color.blue, deep = LEARN.color.blueDeep, size = 48, disabled = false, reduceMotion = false, style }) {
  return (
    <motion.button
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      disabled={disabled}
      whileTap={!disabled && !reduceMotion ? { scale: 0.92, y: 2 } : undefined}
      style={{
        width: size, height: size, borderRadius: "50%", border: "none", flexShrink: 0,
        background: disabled ? "rgba(11,26,51,0.16)" : accent,
        boxShadow: disabled ? "none" : LEARN.shadow.sticker(deep),
        cursor: disabled ? "default" : "pointer", display: "grid", placeItems: "center",
        WebkitTapHighlightColor: "transparent",
        ...style,
      }}
    >
      {children}
    </motion.button>
  );
}

/**
 * In-game count pill ("3 of 8 found"). Floats at the top-right of the engine
 * root; pass `floating={false}` to place it inline (shelf headers).
 */
export function Counter({ done, total, accent = LEARN.color.blue, suffix, label, floating = true, style }) {
  return <CountPill value={Math.min(done, total)} total={total} accent={accent} suffix={suffix} label={label ?? `${done} of ${total}${suffix ? ` ${suffix}` : ""}`} floating={floating} style={style} />;
}

/** Row of stars that fill as the child progresses. */
export function StarRow({ total, done, size = 26 }) {
  return (
    <div aria-label={`${done} of ${total} stars`} style={{ display: "flex", gap: 6, justifyContent: "center", flexWrap: "wrap" }}>
      {Array.from({ length: total }, (_, i) => (
        <motion.span
          key={i}
          initial={false}
          animate={i < done ? { scale: [1, 1.35, 1] } : { scale: 1 }}
          transition={{ duration: 0.4 }}
          style={{ display: "inline-flex" }}
        >
          <IconStar size={size} filled={i < done} />
        </motion.span>
      ))}
    </div>
  );
}

/**
 * A small burst of sparkles from the centre of its (relative) parent.
 * Re-mounts each time `trigger` changes; no-op with reduced motion.
 */
export function SparkleBurst({ trigger = 0, color = LEARN.color.sun, reduceMotion = false, count = 8, distance = 46 }) {
  const parts = useMemo(
    () => Array.from({ length: count }, (_, i) => ({
      i,
      angle: (i / count) * Math.PI * 2 + (trigger % 3) * 0.3,
      dist: distance * (0.75 + ((i * 7 + trigger) % 4) * 0.12),
      size: 6 + ((i * 3 + trigger) % 3) * 3,
    })),
    [count, distance, trigger],
  );
  if (!trigger || reduceMotion) return null;
  return (
    <span key={trigger} aria-hidden="true" style={{ position: "absolute", left: "50%", top: "50%", width: 0, height: 0, pointerEvents: "none", zIndex: 5 }}>
      {parts.map((p) => (
        <motion.span
          key={p.i}
          initial={{ x: 0, y: 0, scale: 0.4, opacity: 1 }}
          animate={{ x: Math.cos(p.angle) * p.dist, y: Math.sin(p.angle) * p.dist, scale: [0.4, 1.1, 0.2], opacity: [1, 1, 0] }}
          transition={{ duration: 0.62, ease: "easeOut" }}
          style={{ position: "absolute", left: -p.size / 2, top: -p.size / 2, width: p.size, height: p.size, borderRadius: p.i % 2 ? "50%" : 2, background: p.i % 3 === 0 ? "#fff" : color, boxShadow: `0 0 6px ${color}` }}
        />
      ))}
    </span>
  );
}

/**
 * Collection shelf: a frosted panel with an accent header (title + count
 * badge) and a soft inner tray of `total` slots that fill, left to right,
 * with the caught stickers (`children`).
 */
export function Tray({ total, children, accent = LEARN.color.blue, deep = LEARN.color.blueDeep, slotSize = 0.72, title, compact = false, style }) {
  const filled = React.Children.count(children);
  const slotRadius = `calc(var(--tile, 72px) * ${slotSize} * 0.24)`;
  return (
    <div
      role="group"
      aria-label={title || "Collected"}
      style={frost({ display: "flex", flexDirection: "column", flexShrink: 0, overflow: "hidden", ...style })}
    >
      <PanelHeader
        accent={accent}
        deep={deep}
        compact={compact}
        badge={<IconStar size={compact ? 16 : 18} color="#fff" stroke="rgba(255,255,255,0.6)" />}
        title={title || "Collected"}
        right={<Counter done={filled} total={total} accent={accent} floating={false} />}
      />
      <div
        style={{
          margin: compact ? 8 : 10,
          padding: compact ? "8px 6px" : "10px 8px",
          borderRadius: LEARN.radius.md,
          background: "rgba(11,26,51,0.035)",
          boxShadow: "inset 0 2px 8px rgba(11,26,51,0.06)",
          display: "flex", gap: compact ? 6 : 8, flexWrap: "wrap", alignItems: "center", justifyContent: "center",
        }}
      >
        {React.Children.map(children, (c) => c)}
        {Array.from({ length: Math.max(0, total - filled) }, (_, i) => (
          <span
            key={`slot-${i}`}
            aria-hidden="true"
            style={{
              width: tileSize(slotSize), height: tileSize(slotSize), borderRadius: slotRadius, boxSizing: "border-box",
              border: `2px dashed ${i === 0 ? accent : `${accent}55`}`, background: i === 0 ? `${accent}12` : "rgba(255,255,255,0.5)", flexShrink: 0,
              transition: "border-color 160ms ease, background 160ms ease",
            }}
          />
        ))}
      </div>
    </div>
  );
}

/** Ghost outline left behind when a tile flies into the tray. */
export function GhostSlot({ size = 1, accent = LEARN.color.blue }) {
  return (
    <span
      aria-hidden="true"
      style={{ display: "block", width: tileSize(size), height: tileSize(size), borderRadius: `calc(var(--tile, 72px) * ${size} * 0.22)`, border: `2px dashed ${accent}55`, background: "rgba(255,255,255,0.18)", boxSizing: "border-box" }}
    />
  );
}

/** Fill a "{word}" template. */
export function fillTemplate(tpl, word) {
  return String(tpl || "{word}").replace(/\{word\}/g, word || "");
}

/**
 * Per-id feedback counters: `flashWrong(id)` / `flashRight(id)` bump a
 * counter for that id so the tile replays its shake / pulse.
 */
export function useTileFeedback() {
  const [shakes, setShakes] = useState({});
  const [pulses, setPulses] = useState({});
  const shakeId = useCallback((id) => setShakes((s) => ({ ...s, [id]: (s[id] || 0) + 1 })), []);
  const pulseId = useCallback((id) => setPulses((s) => ({ ...s, [id]: (s[id] || 0) + 1 })), []);
  return { shakes, pulses, shakeId, pulseId };
}

/** Normalise config items to { key, word, assetName, isGood, raw }. */
export function normalizeItems(items, letter) {
  const list = (items || []).map((it, i) => ({
    key: `${it?.word || it?.title || it?.id || "item"}-${it?.assetName || i}-${i}`,
    word: it?.word || it?.title || it?.label || "",
    assetName: it?.assetName || it?.imageAssetName || null,
    raw: it,
  }));
  const hasFlags = list.some((x) => x.raw?.isTarget != null || x.raw?.isCorrect != null);
  const L = String(letter || "").toLowerCase();
  const startsWithLetter = (w) => !!L && String(w || "").toLowerCase().startsWith(L);
  const mixed = !hasFlags && L && list.some((x) => startsWithLetter(x.word)) && list.some((x) => !startsWithLetter(x.word));
  return list.map((x) => ({
    ...x,
    isGood: hasFlags
      ? x.raw.isCorrect === true || x.raw.isTarget === true
      : x.raw?.distractor === true
        ? false
        : mixed
          ? startsWithLetter(x.word)
          : true,
  }));
}

/**
 * Grid style for a handful of answer tiles: 2×2 on phones when there are
 * four, one row otherwise; wraps naturally past four.
 */
export function choiceGrid(n, compact) {
  const cols = n > 4 ? Math.ceil(n / 2) : compact && n === 4 ? 2 : Math.max(1, n);
  return {
    display: "grid",
    gridTemplateColumns: `repeat(${cols}, max-content)`,
    justifyContent: "center",
    alignItems: "start",
    gap: "calc(var(--tile, 72px) * 0.18)",
    maxWidth: "100%",
  };
}

/** Tile multiplier that keeps N tiles comfortable in both arenas. */
export function tileMultForCount(n) {
  if (n <= 4) return 1.5;
  if (n <= 6) return 1.35;
  if (n <= 9) return 1.2;
  if (n <= 12) return 1.08;
  return 0.98;
}

/** Cheap hook: fires `cb` once, `ms` after `when` becomes true. */
export function useDelayedOnce(when, ms, cb) {
  const fired = useRef(false);
  useEffect(() => {
    if (!when || fired.current) return undefined;
    fired.current = true;
    const t = setTimeout(cb, ms);
    return () => clearTimeout(t);
  }, [when, ms, cb]);
}
